/**
 * useLiveStatus — single source of truth for "are we live right now?"
 *
 * Multiple surfaces consume this signal:
 *   • /live page — decides between iframe vs offline poster.
 *   • Top navbar `<LiveNowPill />` — only renders when live, drives
 *     conversion by surfacing the broadcast on every page (incl. mobile).
 *   • (future) email signup / hero ribbons can subscribe for free.
 *
 * Implementation goals:
 *   • ONE network poll for the whole app, regardless of how many
 *     components mount the hook. Each `useLiveStatus()` callsite is
 *     effectively free after the first — they all read the same module-
 *     level cache and the same setInterval.
 *   • Survive a network blip without yanking the navbar pill (we keep
 *     the last good state through fetch failures and only flip offline
 *     when the wire payload says so).
 *   • Liveness has exactly ONE source of truth: the backend YouTube
 *     auto-detect endpoint at `/api/v1/live/youtube-status`. We're
 *     intentionally NOT mixing in editorial flags from
 *     `live-state.json` (no manual `live.isLive` override, no
 *     `status.state !== "OFFLINE"` fallback) because every additional
 *     source is one more thing to forget to update — exactly the
 *     "stale LIVE flashing during an outage" failure mode we hit on
 *     2026-05-03.
 *
 *     `isLive` resolves like this:
 *       - Backend reachable AND `enabled=true` AND `live=true`  → true
 *       - Anything else (offline, disabled, unreachable, no API key) → false
 *
 *     The granular `status.state` field in live-state.json is still
 *     used during a live broadcast to drive the LIVE / ARB FOUND /
 *     PLACING BET / WON pulse-pill color and the toast cycle on the
 *     /live page — but ONLY when `isLive=true`. When YouTube says
 *     we're offline, status.state is ignored end-to-end.
 *
 *     `live.posterUrl` (offline poster art) is also still consumed
 *     from the JSON, but that's a render-time asset, not a liveness
 *     signal.
 */
import { useEffect, useState } from "react";

const LIVE_STATE_URL = "/live-state.json";
const LIVE_STATE_POLL_MS = 30_000;

const API_BASE_URL = (process.env.REACT_APP_BOOKIE_BRAWLER_API_BASE_URL || "").replace(/\/$/, "");
const YT_STATUS_URL = `${API_BASE_URL}/api/v1/live/youtube-status`;

export type LiveStatusState =
  | "OFFLINE"
  | "LIVE"
  | "SEARCHING"
  | "ARB FOUND"
  | "PLACING BET"
  | "WON";

export type StreamSource = "restream" | "youtube";

export interface LiveStateDoc {
  show?: { day?: number; headline?: string; hostHandle?: string };
  status?: { state?: LiveStatusState | string; detail?: string };
  /** OBS overlay only. The /live React page intentionally does NOT
   *  render these — keeping them in sync on two surfaces was too much
   *  per-stream churn, and the host already updates them live in the
   *  on-screen overlay. Field is typed here purely to describe the
   *  wire schema accurately for `live.raw` debugging. */
  stats?: {
    profitToday?: number;
    profitMonth?: number;
    /** Deprecated alias for profitMonth — kept for legacy payloads. */
    profitWeek?: number;
    arbsPlaced?: number;
    currency?: string;
  };
  ticker?: string[];
  schedule?: { nextLiveISO?: string | null; cadence?: string };
  streamSource?: StreamSource | null;
  restreamEmbedToken?: string | null;
  /** Render-time live-broadcast assets. Liveness itself is decided
   *  by the backend YouTube auto-detect — this object only configures
   *  what the offline state looks like. */
  live?: {
    /** Image rendered in place of the player iframe when offline.
     *  Absolute URL or root-relative path. Empty/null → CSS gradient. */
    posterUrl?: string | null;
    /** Optional true broadcast start, set by ops when the show goes
     *  live (e.g. the OBS "go live" hook writes it). When present and
     *  parseable, the /live page's REC timecode counts up from this
     *  exact instant — the most accurate "session uptime" we can show.
     *  When absent, the page falls back to a per-broadcast localStorage
     *  anchor (first time THIS device observed the stream live), which
     *  is monotonic and survives reloads but starts from first view.
     *  ISO-8601, e.g. "2026-06-22T01:30:00Z". */
    startedAtISO?: string | null;
  };
}

/** Wire shape for /api/v1/live/youtube-status (mirrors the Java
 *  YouTubeLiveStatusDTO). Jackson serializes with snake_case keys. */
export interface YouTubeLiveStatusWire {
  enabled: boolean;
  live: boolean;
  video_id: string | null;
  title: string | null;
  thumbnail_url: string | null;
  channel_id: string | null;
  last_checked_at_ms: number | null;
  stale: boolean;
  reason: string | null;
}

export interface LiveStatusSnapshot {
  /** True iff we're live right now. Highest-priority source wins
   *  (backend YouTube auto-detect → manual override → derived). */
  isLive: boolean;
  /** Granular status state — drives the pulsing pill color and toast cycle. */
  state: LiveStatusState;
  headline: string;
  posterUrl: string | null;
  nextLiveISO: string | null;
  /** Which signal flipped `isLive` to its current value. Useful for
   *  debug overlays and analytics. With JSON overrides removed this
   *  collapses to two cases: `youtube` when the backend auto-detect
   *  fired, `default` for everything else (offline / unreachable /
   *  not configured). */
  source: "youtube" | "default";
  /** YouTube-specific extras (only populated when the backend signal
   *  is enabled and live; null otherwise). Surfaces deep-link info to
   *  the navbar pill and the /live page. */
  youtubeVideoId: string | null;
  youtubeTitle: string | null;
  youtubeThumbnailUrl: string | null;
  /** Full underlying doc — escape hatch for components that need fields
   *  outside this hook's curated surface (e.g. live-page wants stats). */
  raw: LiveStateDoc | null;
  /** False until the first successful fetch lands; lets consumers avoid
   *  flashing "OFFLINE" on first paint when we just haven't loaded yet. */
  loaded: boolean;
}

const FALLBACK_HEADLINE = "Daily live arb challenge";

const FALLBACK_SNAPSHOT: LiveStatusSnapshot = {
  isLive: false,
  state: "OFFLINE",
  headline: FALLBACK_HEADLINE,
  posterUrl: null,
  nextLiveISO: null,
  source: "default",
  youtubeVideoId: null,
  youtubeTitle: null,
  youtubeThumbnailUrl: null,
  raw: null,
  loaded: false,
};

const deriveSnapshot = (
  doc: LiveStateDoc | null,
  yt: YouTubeLiveStatusWire | null,
): LiveStatusSnapshot => {
  const safeDoc = doc ?? {};

  // Single liveness source: backend YouTube auto-detect.
  // Anything other than an explicit `enabled=true, live=true` is
  // treated as offline. This intentionally fails closed for every
  // edge case (backend down, API key missing, network blip, etc.)
  // so the navbar pill never lies.
  const isLive = !!(yt && yt.enabled && yt.live === true);
  const source: LiveStatusSnapshot["source"] = isLive ? "youtube" : "default";

  // The granular `status.state` is only meaningful while we're
  // actually broadcasting — it drives the toast cycle (ARB FOUND /
  // PLACING BET / WON). When YouTube says we're offline we force
  // it to "OFFLINE" regardless of what the JSON contains, so the
  // /live page's status pill can never disagree with the navbar pill.
  const knownStates: LiveStatusState[] = [
    "OFFLINE",
    "LIVE",
    "SEARCHING",
    "ARB FOUND",
    "PLACING BET",
    "WON",
  ];
  const rawState = (safeDoc.status?.state ?? "OFFLINE").toString().toUpperCase();
  const stateFromDoc: LiveStatusState = (knownStates as string[]).includes(rawState)
    ? (rawState as LiveStatusState)
    : "OFFLINE";
  const state: LiveStatusState = isLive ? stateFromDoc : "OFFLINE";

  // Headline: prefer YouTube's broadcast title when live (real-time
  // and viewer-facing), fall back to the editorial headline, then
  // a generic constant. None of these affect liveness.
  const ytTitle = yt?.title?.trim() || null;
  const editorialHeadline = safeDoc.show?.headline?.trim() || null;
  const headline =
    (isLive && ytTitle) || editorialHeadline || FALLBACK_HEADLINE;

  return {
    isLive,
    state,
    headline,
    posterUrl: safeDoc.live?.posterUrl?.trim() || null,
    nextLiveISO: safeDoc.schedule?.nextLiveISO ?? null,
    source,
    youtubeVideoId: yt?.video_id ?? null,
    youtubeTitle: ytTitle,
    youtubeThumbnailUrl: yt?.thumbnail_url ?? null,
    raw: doc,
    loaded: true,
  };
};

// ─── Module-level singleton: one poller, many subscribers ───
type Listener = (snap: LiveStatusSnapshot) => void;
const listeners = new Set<Listener>();
let currentSnapshot: LiveStatusSnapshot = FALLBACK_SNAPSHOT;
let pollTimer: number | null = null;
let inflight: Promise<void> | null = null;

// Last-known wire payloads. Each fetch updates its own slot
// independently so a transient failure on one source never zeroes out
// the other (e.g. backend hiccup shouldn't yank manual override info).
let lastDoc: LiveStateDoc | null = null;
let lastYt: YouTubeLiveStatusWire | null = null;

const emit = (snap: LiveStatusSnapshot) => {
  currentSnapshot = snap;
  listeners.forEach((l) => l(snap));
};

const fetchLiveStateDoc = async (): Promise<LiveStateDoc | null> => {
  try {
    const res = await fetch(`${LIVE_STATE_URL}?t=${Date.now()}`, {
      cache: "no-store",
    });
    if (!res.ok) return null;
    return (await res.json()) as LiveStateDoc;
  } catch {
    return null;
  }
};

const fetchYouTubeStatus = async (): Promise<YouTubeLiveStatusWire | null> => {
  // Skip the call entirely if no API base is configured (e.g.
  // production build that intentionally doesn't talk to the backend).
  // Falls back through to the manual / derived chain.
  if (!API_BASE_URL) return null;
  try {
    const res = await fetch(YT_STATUS_URL, { cache: "no-store" });
    if (!res.ok) return null;
    return (await res.json()) as YouTubeLiveStatusWire;
  } catch {
    return null;
  }
};

const fetchOnce = async (): Promise<void> => {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      // Fan both fetches out in parallel — neither blocks the other,
      // so the slowest of the two sets the polling cadence ceiling.
      const [doc, yt] = await Promise.all([
        fetchLiveStateDoc(),
        fetchYouTubeStatus(),
      ]);
      // Only overwrite a slot when its fetch actually succeeded.
      if (doc) lastDoc = doc;
      if (yt) lastYt = yt;
      emit(deriveSnapshot(lastDoc, lastYt));
    } catch {
      // Keep last good snapshot — never flap the navbar pill on a
      // transient network failure.
    } finally {
      inflight = null;
    }
  })();
  return inflight;
};

const ensurePoller = () => {
  if (pollTimer !== null || typeof window === "undefined") return;
  fetchOnce();
  pollTimer = window.setInterval(fetchOnce, LIVE_STATE_POLL_MS);
};

const stopPollerIfIdle = () => {
  if (listeners.size > 0) return;
  if (pollTimer !== null && typeof window !== "undefined") {
    window.clearInterval(pollTimer);
    pollTimer = null;
  }
};

export const useLiveStatus = (): LiveStatusSnapshot => {
  const [snap, setSnap] = useState<LiveStatusSnapshot>(currentSnapshot);

  useEffect(() => {
    listeners.add(setSnap);
    ensurePoller();
    // Sync to current value in case poller already fetched before we mounted.
    setSnap(currentSnapshot);
    return () => {
      listeners.delete(setSnap);
      stopPollerIfIdle();
    };
  }, []);

  return snap;
};

/** Test/SSR escape hatch — lets non-React surfaces (e.g. analytics
 *  beacons) read the same cached snapshot without standing up a hook. */
export const peekLiveStatus = (): LiveStatusSnapshot => currentSnapshot;
